import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, unref as _unref, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "asel" }
const _hoisted_2 = {
  class: "asel__filters",
  "data-tour": "admin-sellers-kpis"
}
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "asel__filter-label" }
const _hoisted_5 = { class: "asel__filter-count" }
const _hoisted_6 = {
  class: "sui-card sui-card--flush",
  "data-tour": "admin-sellers-list"
}
const _hoisted_7 = {
  class: "sui-toolbar",
  "data-tour": "admin-sellers-toolbar"
}
const _hoisted_8 = { class: "sui-search" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = {
  key: 0,
  class: "asel__error"
}
const _hoisted_11 = {
  key: 1,
  class: "sui-table-wrap"
}
const _hoisted_12 = { class: "sui-table" }
const _hoisted_13 = { class: "sui-table__strong" }
const _hoisted_14 = { class: "sui-table__sub" }
const _hoisted_15 = { class: "sui-table__sub" }
const _hoisted_16 = { class: "sui-table__sub" }
const _hoisted_17 = { class: "is-num" }
const _hoisted_18 = { key: 1 }
const _hoisted_19 = { class: "is-center" }
const _hoisted_20 = ["onClick"]
const _hoisted_21 = { class: "is-center" }
const _hoisted_22 = {
  key: 0,
  class: "asel__reason"
}
const _hoisted_23 = { class: "is-center" }
const _hoisted_24 = { class: "asel__actions" }
const _hoisted_25 = ["disabled", "title", "onClick"]
const _hoisted_26 = ["onClick"]
const _hoisted_27 = ["onClick"]
const _hoisted_28 = {
  key: 2,
  class: "sui-empty"
}
const _hoisted_29 = {
  key: 0,
  class: "asel__modal",
  role: "dialog",
  "aria-modal": "true"
}
const _hoisted_30 = { class: "asel__modal-grid" }
const _hoisted_31 = ["disabled"]

import { computed, onMounted, reactive, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  changeSellerStatus,
  createAdminSeller,
  fetchAdminLegalDocs,
  fetchAdminSellers,
  fetchSellerAcceptances,
  type AdminSellerRow,
} from '@/api/admin'
import { useAdminProducts } from '@/composables/useAdminProducts'
import { SELLER_STATUS_LABEL, type SellerStatus } from '@/constants/sellerSession'
import {
  mergeLegalDocViews,
  openLegalAcceptancesPreview,
} from '@/utils/legalAcceptancePreview'


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminSellersPage',
  setup(__props) {

const { notify } = useAdminProducts()

const sellers = ref<AdminSellerRow[]>([])
const loading = ref(false)
const errorMessage = ref('')
const keyword = ref('')
const statusFilter = ref<string>('all')
const createOpen = ref(false)
const creating = ref(false)
const createForm = reactive({
  sellerId: '',
  tempPassword: '',
})

function sellerLabel(seller: AdminSellerRow) {
  return seller.storeName || seller.sellerId || seller.sellerCode
}

const STATUS_TONE: Record<SellerStatus, string> = {
  PENDING: 'sui-badge--violet',
  APPROVED: 'sui-badge--green',
  REJECTED: 'sui-badge--red',
  SUSPENDED: 'sui-badge--amber',
  CLOSED: 'sui-badge--gray',
}

function statusTone(status: SellerStatus): string {
  return STATUS_TONE[status] ?? 'sui-badge--gray'
}

async function load() {
  loading.value = true
  errorMessage.value = ''

  try {
    sellers.value = await fetchAdminSellers()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '판매업체를 불러오지 못했습니다.'
    sellers.value = []
  } finally {
    loading.value = false
  }
}

onMounted(load)

const statusTabs = computed(() => {
  const countOf = (key: SellerStatus | 'all') =>
    key === 'all' ? sellers.value.length : sellers.value.filter((row) => row.status === key).length

  return [
    { key: 'all', label: '전체', count: countOf('all') },
    { key: 'PENDING', label: '승인 대기', count: countOf('PENDING') },
    { key: 'APPROVED', label: '이용중', count: countOf('APPROVED') },
    { key: 'SUSPENDED', label: '정지', count: countOf('SUSPENDED') },
    { key: 'REJECTED', label: '반려', count: countOf('REJECTED') },
    { key: 'CLOSED', label: '해지', count: countOf('CLOSED') },
  ]
})

const filtered = computed(() => {
  const query = keyword.value.toLowerCase()

  return sellers.value.filter((seller) => {
    if (statusFilter.value !== 'all' && seller.status !== statusFilter.value) return false
    if (!query) return true
    return (
      String(seller.storeName || '')
        .toLowerCase()
        .includes(query) ||
      String(seller.bizName || '')
        .toLowerCase()
        .includes(query) ||
      String(seller.bizNumber || '').includes(query) ||
      String(seller.sellerId || '')
        .toLowerCase()
        .includes(query) ||
      String(seller.sellerCode || '')
        .toLowerCase()
        .includes(query)
    )
  })
})

async function apply(seller: AdminSellerRow, status: string, reason = '') {
  try {
    await changeSellerStatus(seller.sellerCode, status, reason)
    await load()
    notify(`${sellerLabel(seller)} 상태를 바꿨습니다.`)
  } catch (error) {
    notify(error instanceof Error ? error.message : '상태를 바꾸지 못했습니다.')
  }
}

function makeTempPassword() {
  return `Tmp${Math.random().toString(36).slice(2, 8)}!a1`
}

function openCreate() {
  createForm.sellerId = ''
  createForm.tempPassword = makeTempPassword()
  createOpen.value = true
}

async function submitCreate() {
  creating.value = true
  try {
    const seller = await createAdminSeller({
      sellerId: createForm.sellerId,
      tempPassword: createForm.tempPassword,
    })
    createOpen.value = false
    await load()
    notify(
      `등록 완료. 아이디 ${seller.sellerId} / 임시비번 ${createForm.tempPassword}`,
    )
  } catch (error) {
    notify(error instanceof Error ? error.message : '등록에 실패했습니다.')
  } finally {
    creating.value = false
  }
}

async function showAccepts(seller: AdminSellerRow) {
  try {
    const [accepts, docs] = await Promise.all([
      fetchSellerAcceptances(seller.sellerCode),
      fetchAdminLegalDocs('SELLER'),
    ])
    const activeDocs = docs.filter((doc) => doc.active)
    const views = mergeLegalDocViews(accepts, activeDocs)
    if (!views.length) {
      window.alert('등록된 계약서·약관이 없습니다. 약관 CMS에서 활성 계약서를 등록해 주세요.')
      return
    }
    const signed = accepts.find((row) => row.signedAt || row.signature)
    const opened = openLegalAcceptancesPreview({
      heading: `${sellerLabel(seller)} 계약서`,
      subtitle: signed
        ? `${seller.sellerId} · 최근 동의 ${signed.signedAt || '-'}`
        : `${seller.sellerId} · 미서명 · 본문 열람`,
      accepts: views,
      party: {
        storeName: seller.storeName || seller.bizName,
        ownerName: seller.ownerName,
        bizNumber: seller.bizNumber,
        address: seller.address,
        addressDetail: seller.addressDetail,
      },
      downloadStoredPdf: async (row) => {
        if (!row.idx) throw new Error('저장된 계약서 PDF가 없습니다.')
        const { downloadSellerAcceptancePdf } = await import('@/api/admin')
        await downloadSellerAcceptancePdf(
          seller.sellerCode,
          row.idx,
          `${row.title || row.code}_v${row.version}.pdf`,
        )
      },
    })
    if (!opened) {
      notify('팝업이 차단되어 계약서를 열 수 없습니다.')
    }
  } catch (error) {
    notify(error instanceof Error ? error.message : '계약서를 불러오지 못했습니다.')
  }
}

function onApprove(seller: AdminSellerRow) {
  if (seller.mustOnboard) {
    notify('온보딩을 완료한 뒤에만 승인할 수 있습니다.')
    return
  }
  if (!window.confirm(`${sellerLabel(seller)}을(를) 승인할까요? 승인하면 상품 등록을 요청할 수 있습니다.`)) {
    return
  }
  apply(seller, 'APPROVED')
}

function onReject(seller: AdminSellerRow) {
  const reason = window.prompt('반려 사유를 적어 주세요.', seller.rejectReason || '')
  if (!reason?.trim()) {
    return
  }
  if (!window.confirm(`${sellerLabel(seller)}을(를) 반려할까요?`)) return
  apply(seller, 'REJECTED', reason.trim())
}

function onSuspend(seller: AdminSellerRow) {
  const reason = window.prompt('정지 사유를 적어 주세요.', '')
  if (!reason?.trim()) {
    return
  }
  if (!window.confirm(`${sellerLabel(seller)}을(를) 정지할까요?`)) return
  apply(seller, 'SUSPENDED', reason.trim())
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(statusTabs.value, (tab) => {
        return (_openBlock(), _createElementBlock("button", {
          key: tab.key,
          type: "button",
          class: _normalizeClass(["sui-card asel__filter", { 'is-active': statusFilter.value === tab.key, 'is-alert': tab.key === 'PENDING' && tab.count > 0 }]),
          onClick: ($event: any) => (statusFilter.value = tab.key)
        }, [
          _createElementVNode("span", _hoisted_4, _toDisplayString(tab.label), 1),
          _createElementVNode("span", _hoisted_5, _toDisplayString(tab.count), 1)
        ], 10, _hoisted_3))
      }), 128))
    ]),
    _createElementVNode("section", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("label", _hoisted_8, [
          _createVNode(AppIcon, {
            icon: "mdi-magnify",
            size: 17
          }),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((keyword).value = $event)),
            type: "search",
            placeholder: "업체명 · 사업자번호 · 아이디 검색"
          }, null, 512), [
            [
              _vModelText,
              keyword.value,
              void 0,
              { trim: true }
            ]
          ])
        ]),
        _cache[8] || (_cache[8] = _createElementVNode("span", { class: "sui-toolbar__spacer" }, null, -1)),
        _createElementVNode("button", {
          type: "button",
          class: "sui-btn sui-btn--primary",
          "data-tour": "admin-sellers-create",
          onClick: openCreate
        }, [
          _createVNode(AppIcon, {
            icon: "mdi-plus",
            size: 16
          }),
          _cache[6] || (_cache[6] = _createTextVNode(" 판매업체 등록 ", -1))
        ]),
        _createElementVNode("button", {
          type: "button",
          class: "sui-btn",
          disabled: loading.value,
          onClick: load
        }, [
          _createVNode(AppIcon, {
            icon: "mdi-refresh",
            size: 16
          }),
          _cache[7] || (_cache[7] = _createTextVNode(" 새로고침 ", -1))
        ], 8, _hoisted_9)
      ]),
      (errorMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_10, [
            _createVNode(AppIcon, {
              icon: "mdi-alert-outline",
              size: 16
            }),
            _createTextVNode(" " + _toDisplayString(errorMessage.value) + " ", 1),
            _createElementVNode("button", {
              type: "button",
              class: "sui-btn sui-btn--sm",
              onClick: load
            }, "다시 시도")
          ]))
        : _createCommentVNode("", true),
      (filtered.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _createElementVNode("table", _hoisted_12, [
              _cache[9] || (_cache[9] = _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", null, "업체"),
                  _createElementVNode("th", null, "사업자"),
                  _createElementVNode("th", null, "연락처"),
                  _createElementVNode("th", { class: "is-num" }, "등록 상품"),
                  _createElementVNode("th", { class: "is-center" }, "온보딩"),
                  _createElementVNode("th", { class: "is-center" }, "상태"),
                  _createElementVNode("th", { class: "is-center" }, "처리")
                ])
              ], -1)),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filtered.value, (seller) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: seller.sellerCode
                  }, [
                    _createElementVNode("td", null, [
                      _createElementVNode("span", _hoisted_13, _toDisplayString(seller.storeName || seller.sellerId), 1),
                      _createElementVNode("span", _hoisted_14, _toDisplayString(seller.sellerCode) + " · " + _toDisplayString(seller.sellerId), 1)
                    ]),
                    _createElementVNode("td", null, [
                      _createElementVNode("span", null, _toDisplayString(seller.bizName || (seller.mustOnboard ? '온보딩 대기' : '—')), 1),
                      _createElementVNode("span", _hoisted_15, [
                        (seller.bizNumber || seller.ownerName)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                              _createTextVNode(_toDisplayString(seller.bizNumber || '—') + " · " + _toDisplayString(seller.ownerName || '—'), 1)
                            ], 64))
                          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                              _createTextVNode("—")
                            ], 64))
                      ])
                    ]),
                    _createElementVNode("td", null, [
                      _createElementVNode("span", null, _toDisplayString(seller.phone || '—'), 1),
                      _createElementVNode("span", _hoisted_16, _toDisplayString(seller.email || '—'), 1)
                    ]),
                    _createElementVNode("td", _hoisted_17, [
                      (seller.productCount > 0)
                        ? (_openBlock(), _createBlock(_component_router_link, {
                            key: 0,
                            class: "asel__count-link",
                            to: {
                    path: '/admin/products',
                    query: { keyword: seller.storeName || seller.sellerCode },
                  }
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(seller.productCount) + "개 ", 1)
                            ]),
                            _: 2
                          }, 1032, ["to"]))
                        : (_openBlock(), _createElementBlock("span", _hoisted_18, "0개"))
                    ]),
                    _createElementVNode("td", _hoisted_19, [
                      _createElementVNode("span", {
                        class: _normalizeClass(["sui-badge", seller.mustOnboard ? 'sui-badge--amber' : 'sui-badge--green'])
                      }, _toDisplayString(seller.mustOnboard ? '미완료' : '완료'), 3),
                      _createElementVNode("button", {
                        type: "button",
                        class: "asel__sign-link",
                        "data-tour": "admin-sellers-contract",
                        onClick: ($event: any) => (showAccepts(seller))
                      }, _toDisplayString(seller.mustOnboard ? '계약서' : '계약·서명'), 9, _hoisted_20)
                    ]),
                    _createElementVNode("td", _hoisted_21, [
                      _createElementVNode("span", {
                        class: _normalizeClass(["sui-badge", statusTone(seller.status)])
                      }, _toDisplayString(_unref(SELLER_STATUS_LABEL)[seller.status]), 3),
                      (seller.rejectReason)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_22, _toDisplayString(seller.rejectReason), 1))
                        : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("td", _hoisted_23, [
                      _createElementVNode("div", _hoisted_24, [
                        (seller.status !== 'APPROVED')
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              type: "button",
                              class: "sui-btn sui-btn--sm sui-btn--primary",
                              disabled: Boolean(seller.mustOnboard),
                              title: seller.mustOnboard ? '온보딩 완료 후 승인 가능' : '',
                              onClick: ($event: any) => (onApprove(seller))
                            }, " 승인 ", 8, _hoisted_25))
                          : _createCommentVNode("", true),
                        (seller.status !== 'REJECTED')
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 1,
                              type: "button",
                              class: "sui-btn sui-btn--sm",
                              onClick: ($event: any) => (onReject(seller))
                            }, " 반려 ", 8, _hoisted_26))
                          : _createCommentVNode("", true),
                        (seller.status === 'APPROVED')
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 2,
                              type: "button",
                              class: "sui-btn sui-btn--sm sui-btn--ghost",
                              onClick: ($event: any) => (onSuspend(seller))
                            }, " 정지 ", 8, _hoisted_27))
                          : _createCommentVNode("", true)
                      ])
                    ])
                  ]))
                }), 128))
              ])
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_28, [
            _createVNode(AppIcon, {
              icon: "mdi-storefront-outline",
              size: 32
            }),
            _createElementVNode("p", null, _toDisplayString(loading.value ? '불러오는 중입니다…' : '조건에 맞는 판매업체가 없습니다.'), 1)
          ]))
    ]),
    (createOpen.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
          _createElementVNode("div", {
            class: "asel__modal-backdrop",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (createOpen.value = false))
          }),
          _createElementVNode("form", {
            class: "asel__modal-panel",
            onSubmit: _withModifiers(submitCreate, ["prevent"])
          }, [
            _createElementVNode("header", null, [
              _cache[10] || (_cache[10] = _createElementVNode("h2", null, "판매업체 등록", -1)),
              _createElementVNode("button", {
                type: "button",
                class: "sui-btn sui-btn--ghost",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (createOpen.value = false))
              }, "닫기")
            ]),
            _cache[13] || (_cache[13] = _createElementVNode("p", { class: "asel__modal-lead" }, " 아이디와 임시 비밀번호만 등록합니다. 사업자·정산·약관·서명은 업체가 로그인 후 온보딩에서 입력합니다. ", -1)),
            _createElementVNode("div", _hoisted_30, [
              _createElementVNode("label", null, [
                _cache[11] || (_cache[11] = _createElementVNode("span", null, "아이디 *", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((createForm.sellerId) = $event)),
                  required: "",
                  maxlength: "20",
                  placeholder: "영문·숫자·_ 4~20자",
                  pattern: "[A-Za-z0-9_]{4,20}"
                }, null, 512), [
                  [
                    _vModelText,
                    createForm.sellerId,
                    void 0,
                    { trim: true }
                  ]
                ])
              ]),
              _createElementVNode("label", null, [
                _cache[12] || (_cache[12] = _createElementVNode("span", null, "임시 비밀번호 *", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((createForm.tempPassword) = $event)),
                  type: "text",
                  required: "",
                  minlength: "8"
                }, null, 512), [
                  [_vModelText, createForm.tempPassword]
                ])
              ])
            ]),
            _createElementVNode("footer", null, [
              _createElementVNode("button", {
                type: "button",
                class: "sui-btn",
                onClick: _cache[5] || (_cache[5] = ($event: any) => (createOpen.value = false))
              }, "취소"),
              _createElementVNode("button", {
                type: "submit",
                class: "sui-btn sui-btn--primary",
                disabled: creating.value
              }, _toDisplayString(creating.value ? '등록 중…' : '등록'), 9, _hoisted_31)
            ])
          ], 32)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})